import { ReactNode, useEffect, useRef, useState } from "react";
import { SignIn, SignUp, useUser, useClerk } from "@clerk/react";
import { useConvexAuth } from "convex/react";
import { AuthShell } from "./AuthShell";
import { GuestCheckout, ActivatePurchase } from "./GuestCheckout";
import { parseGetParams } from "../funnel/getParams";
import {
  desktopAuthRole,
  desktopAuthProvider,
  desktopSsoCallbackPending,
  DesktopProviderSignIn,
  clearPendingDesktopHandoff,
  DesktopBrowserProviderLaunch,
  DesktopSsoCallback,
  DesktopBrowserCompletion,
} from "./DesktopAuth";

interface SignInGateProps {
  children: ReactNode;
}

// How long "Connecting Steerly" may spin before it's treated as a failure.
// Clerk's SDK has no error callback surfaced through useUser() — a rejected
// bootstrap (offline, blocked network, or a misconfigured publishable key,
// e.g. a production pk_live_ key loaded from the desktop app's
// http://127.0.0.1 origin) just leaves isLoaded false forever. Without this
// deadline the gate is an indefinite silent spinner; the June–July desktop
// releases hung exactly this way for six weeks with no visible error.
const CLERK_LOAD_TIMEOUT_MS = 15_000;

// After sign-in/up, return to the exact URL the visitor landed on — pathname +
// query string — so a /get?plan=…&billing=… deep-link from the marketing site
// survives auth and PricingPage can auto-open the right checkout. Without this,
// Clerk v6 defaults the post-auth redirect to "/", dropping the plan + billing
// period and dumping a ready-to-buy visitor on a generic pricing page — a
// funnel leak at the most important moment. The hash is excluded (Clerk
// hash-routes its own steps). On <SignIn/> the post-sign-in redirect is
// `fallbackRedirectUrl` (NOT `signInFallbackRedirectUrl`, which only exists on
// <AuthenticateWithRedirectCallback>); `signUpFallbackRedirectUrl` covers the
// linked sign-up, and vice-versa on <SignUp/>.
function returnToCurrentUrl(): string {
  return window.location.pathname + window.location.search;
}

// In the desktop app, the embedded Clerk card is NOT shown — social login and
// passkeys don't work in the WebView. Instead the app renders provider
// buttons (Google / Apple / GitHub / email) that all run sign-in in the
// system browser and hand the session back (see DesktopAuth.tsx). On the web,
// the branded split-screen AuthShell wraps a buy-intent-aware card whose
// post-auth redirect returns to the funnel deep-link.
function renderSignIn() {
  const role = desktopAuthRole();
  if (role === "app") {
    return (
      <div className="sign-in-gate">
        <DesktopProviderSignIn />
      </div>
    );
  }
  if (role === "browser") {
    // OAuth return leg: finish the provider flow Clerk started below.
    if (desktopSsoCallbackPending()) return <DesktopSsoCallback />;
    // Provider-preselected tab: bounce straight to that provider.
    if (desktopAuthProvider()) {
      return (
        <div className="sign-in-gate">
          <DesktopBrowserProviderLaunch fallback={<SignIn routing="hash" />} />
        </div>
      );
    }
  }
  // Web app: branded split-screen shell with a buy-intent-aware card that
  // survives the marketing deep-link across auth.
  const returnTo = returnToCurrentUrl();
  // Account setup happens after payment. Preserve activation through either
  // signup or the linked sign-in flow, including external OAuth callbacks.
  const fromPurchase = window.location.pathname === "/activate";
  const card = fromPurchase ? (
    <SignUp routing="hash" forceRedirectUrl={returnTo} signInForceRedirectUrl={returnTo} />
  ) : (
    <SignIn routing="hash" fallbackRedirectUrl={returnTo} signUpFallbackRedirectUrl={returnTo} />
  );
  return <AuthShell>{card}</AuthShell>;
}

export function SignInGate({ children }: SignInGateProps) {
  const { isLoaded: clerkLoaded, isSignedIn } = useUser();
  const { isLoading: convexAuthLoading, isAuthenticated: convexAuthed } = useConvexAuth();
  const clerk = useClerk();

  // Once the app is signed in — by ANY route, not just a completed handoff —
  // drop any persisted desktop handoff. Leaving one behind is an auth hazard,
  // not just clutter: DesktopProviderSignIn re-mounts on every SIGN-OUT, so a
  // stale entry whose ticket is still within its 5-minute TTL would be resumed
  // there and claimed, signing the user straight back in without being asked —
  // and on a shared machine, into whichever account minted that ticket.
  // A deliberate sign-out must stay signed out.
  useEffect(() => {
    if (clerkLoaded && isSignedIn) clearPendingDesktopHandoff();
  }, [clerkLoaded, isSignedIn]);

  // Deadline on the Clerk bootstrap (see CLERK_LOAD_TIMEOUT_MS). The timer
  // only runs while clerkLoaded is false; a late success after the deadline
  // still wins — the timed-out branch below is gated on !clerkLoaded, so the
  // moment Clerk loads, the normal flow renders.
  const [clerkTimedOut, setClerkTimedOut] = useState(false);
  useEffect(() => {
    if (clerkLoaded) return;
    const timer = setTimeout(() => setClerkTimedOut(true), CLERK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [clerkLoaded]);

  // Track whether a Clerk session ALREADY existed when this tab first loaded.
  // SignInGate stays mounted across DesktopBrowserCompletion's unmount/remount
  // (which happens on "Use a different account" → signOut → sign in again), so
  // tracking it here — and resetting to false the moment the user signs out —
  // stops the new session from being mis-read as pre-existing and re-prompting
  // "Continue as <account>?" (Gemini G211).
  const preExistingRef = useRef<boolean | null>(null);
  if (clerkLoaded) {
    if (preExistingRef.current === null) {
      preExistingRef.current = isSignedIn;
    } else if (!isSignedIn) {
      preExistingRef.current = false;
    }
  }

  // Browser side of the desktop handoff (a system-browser tab the app opened):
  // once signed in here, mint a ticket for the app and show "return to Steerly"
  // instead of mounting the full app in the browser tab.
  if (clerkLoaded && isSignedIn && desktopAuthRole() === "browser") {
    return <DesktopBrowserCompletion preExistingSession={preExistingRef.current ?? false} />;
  }

  if (!clerkLoaded) {
    if (clerkTimedOut) {
      return (
        <AuthShell>
          <div className="sign-in-status" role="alert">
            <h1>Steerly can’t reach sign-in</h1>
            <p>
              The sign-in service didn’t respond. Check your internet connection and reload — if
              this keeps happening, the app may be misconfigured for this origin (see the browser
              console for the underlying Clerk error).
            </p>
            <div className="sign-in-status__actions">
              <button type="button" onClick={() => window.location.reload()}>
                Reload
              </button>
            </div>
          </div>
        </AuthShell>
      );
    }
    return (
      <AuthShell>
        <div className="sign-in-status" role="status" aria-live="polite">
          <span className="sign-in-status__spinner" aria-hidden="true" />
          <h1>Connecting Steerly</h1>
          <p>Loading the Clerk session for this browser.</p>
        </div>
      </AuthShell>
    );
  }
  if (
    !isSignedIn &&
    window.location.pathname === "/get" &&
    parseGetParams(window.location.search).plan &&
    !desktopAuthRole()
  )
    return <GuestCheckout />;
  if (!isSignedIn) return renderSignIn();

  if (convexAuthLoading) {
    return (
      <AuthShell>
        <div className="sign-in-status" role="status" aria-live="polite">
          <span className="sign-in-status__spinner" aria-hidden="true" />
          <h1>Connecting Steerly</h1>
          <p>Checking the Convex session for your signed-in workspace.</p>
        </div>
      </AuthShell>
    );
  }

  if (!convexAuthed) {
    return (
      <AuthShell>
        <div className="sign-in-status" role="alert">
          <h1>Convex is not ready</h1>
          <p>
            Steerly loaded Clerk, but Convex did not accept the session. Run the Convex dev/deploy
            step and verify the Clerk JWT template named convex, then reload the app.
          </p>
          <div className="sign-in-status__actions">
            <button type="button" onClick={() => window.location.reload()}>
              Reload
            </button>
            <button type="button" onClick={() => void clerk.signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (window.location.pathname === "/activate") return <ActivatePurchase />;
  return <>{children}</>;
}
